/**
 * Thin fetch wrapper for the FastAPI backend.
 *
 * - Uses relative URLs so Vite's dev proxy and the production reverse proxy
 *   both work without configuration changes.
 * - The bearer token is read from a process-level holder so React Query
 *   query functions (which are pure) don't have to take an `auth` argument.
 *   `AuthTokenSync` keeps the holder in sync with the OIDC user.
 * - Throws an Error with the parsed FastAPI detail when the response is not
 *   2xx, so React Query surfaces a useful message.
 */

const BASE_URL = "/api/v1";

let currentToken: string | null = null;

export function setAccessToken(token: string | null): void {
  currentToken = token;
}

export interface ApiErrorPayload {
  detail?: string | Record<string, unknown>;
}

export class ApiError extends Error {
  readonly status: number;
  readonly detail: string | Record<string, unknown> | undefined;

  constructor(status: number, message: string, detail?: ApiErrorPayload["detail"]) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.detail = detail;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const headers: Record<string, string> = {
    Accept: "application/json",
    ...(init?.body ? { "Content-Type": "application/json" } : {}),
    ...((init?.headers as Record<string, string> | undefined) ?? {}),
  };
  if (currentToken && !headers.Authorization) {
    headers.Authorization = `Bearer ${currentToken}`;
  }
  const response = await fetch(`${BASE_URL}${path}`, {
    ...init,
    headers,
  });

  if (!response.ok) {
    let payload: ApiErrorPayload | undefined;
    try {
      payload = (await response.json()) as ApiErrorPayload;
    } catch {
      payload = undefined;
    }
    const detail = payload?.detail;
    const message =
      typeof detail === "string" ? detail : `request failed with status ${response.status}`;
    throw new ApiError(response.status, message, detail);
  }

  if (response.status === 204) {
    return undefined as T;
  }
  return (await response.json()) as T;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body: unknown) =>
    request<T>(path, { method: "POST", body: JSON.stringify(body) }),
  patch: <T>(path: string, body: unknown) =>
    request<T>(path, { method: "PATCH", body: JSON.stringify(body) }),
  delete: <T>(path: string) => request<T>(path, { method: "DELETE" }),
};
